/**
 * Correspondence — every email the module sent and every reply that landed
 * (operator order 2026-09-10): scheduled cycle emails, reminders,
 * escalations, one-off requests, Workstream follow-ups, and inbound replies.
 * A reply that carried PHI or the universe itself is destroyed with PURGE, on
 * an attestation; the row keeps the fact and the certificate.
 *
 * Organized so it stays readable as months pass (operator order 2026-10-06):
 * one month at a time (the month the request went out, so a request, its
 * reminders and its replies stay together), a search across subject,
 * recipients, department, FDR and request, and one folder per request with
 * its recipient, reporting period, status, due date and counts. Requests that
 * need attention (open and overdue, or a send that failed) come first. A
 * reminder can be sent by hand from any open request.
 */
import React, { useEffect, useMemo, useState } from 'react';
import ManualReminderDialog from './ManualReminderDialog';

const API = process.env.REACT_APP_API_URL;

const KIND_LABEL = {
  cycle_invitation: 'Scheduled request', request_sent: 'Request sent', followup: 'Workstream follow-up',
  compliance_escalation: 'Escalation to compliance', reply: 'Reply received',
  reminder_72h_before: 'Reminder, due in 3 days', reminder_24h_before: 'Reminder, due tomorrow',
  reminder_due_today: 'Reminder, due today', reminder_overdue_daily: 'Reminder, overdue',
  reminder_manual: 'Reminder sent by hand',
};
const SOURCE_LABEL = { schedule: 'Scheduler', reminder: 'Scheduler', escalation: 'Scheduler', manual: 'Sent by hand', workstream: 'Workstream', inbound: 'Inbound' };
const STATUS_LABEL = { draft: 'Draft', sent: 'Open', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled', expired: 'Expired' };
const OPEN = new Set(['sent', 'in_progress']);

const FILTERS = [
  ['all', 'All'], ['attention', 'Needs attention'], ['manual', 'Sent by hand'], ['reminder', 'Reminders'],
  ['schedule', 'Scheduler'], ['workstream', 'Workstream'], ['inbound', 'Replies'], ['failed', 'Failed'],
];

const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, 15)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};
const periodLabel = (s, e) => {
  if (!s || !e) return null;
  const a = String(s).slice(0, 10);
  const b = String(e).slice(0, 10);
  const opts = (m) => ({ month: m, year: 'numeric', timeZone: 'UTC' });
  const ds = new Date(`${a}T12:00:00Z`);
  const de = new Date(`${b}T12:00:00Z`);
  if (a.slice(0, 7) === b.slice(0, 7)) return ds.toLocaleDateString('en-US', opts('long'));
  return `${ds.toLocaleDateString('en-US', opts('short'))} to ${de.toLocaleDateString('en-US', opts('short'))}`;
};
const isReminder = (r) => String(r.kind || '').startsWith('reminder_');

function rowMatches(r, filter) {
  if (filter === 'all' || filter === 'attention') return true;
  if (filter === 'inbound') return r.direction === 'inbound';
  if (filter === 'failed') return r.direction === 'outbound' && !r.sent_ok;
  if (filter === 'reminder') return isReminder(r);
  if (filter === 'schedule') return r.source === 'schedule' || r.source === 'reminder' || r.source === 'escalation';
  return r.source === filter;
}

export default function Correspondence({ requestId = null }) {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(null);
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [remindFor, setRemindFor] = useState(null);

  // Months that have mail, newest first; the tab opens on the newest.
  const loadMonths = () => {
    if (requestId) return;
    fetch(`${API}/api/universe-requests/correspondence/months`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        const list = d.months || [];
        setMonths(list);
        setMonth((cur) => cur || (list[0]?.month || 'all'));
      })
      .catch(() => setMonth((cur) => cur || 'all'));
  };
  useEffect(loadMonths, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search runs after a short pause in typing.
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = () => {
    if (!requestId && !month) return;
    setLoading(true);
    const p = new URLSearchParams();
    if (requestId) p.set('request_id', requestId);
    else if (month && month !== 'all') p.set('month', month);
    if (q) p.set('q', q);
    fetch(`${API}/api/universe-requests/correspondence?${p.toString()}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setRows(d.correspondence || []); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [requestId, month, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // One folder per request; an email with no request files under its schedule.
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const r of rows) {
      const key = r.request_id || `schedule:${r.schedule_id || 'none'}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          requestId: r.request_id || null,
          title: r.request_title || r.schedule_name || 'Not linked to a request',
          recipient: r.recipient_label || null,
          period: periodLabel(r.reporting_period_start, r.reporting_period_end),
          status: r.request_status || null,
          dueAt: r.request_due_at || null,
          rows: [],
        });
      }
      byKey.get(key).rows.push(r);
    }
    const now = Date.now();
    const list = [...byKey.values()].map((g) => {
      const out = g.rows.filter((r) => r.direction === 'outbound');
      const failed = out.filter((r) => !r.sent_ok).length;
      const open = g.status ? OPEN.has(g.status) : false;
      const overdue = !!(open && g.dueAt && new Date(g.dueAt).getTime() < now);
      return {
        ...g,
        open,
        overdue,
        failed,
        counts: {
          sent: out.length,
          reminders: g.rows.filter(isReminder).length,
          byHand: g.rows.filter((r) => r.source === 'manual').length,
          replies: g.rows.filter((r) => r.direction === 'inbound').length,
        },
        attention: overdue || failed > 0,
        // The conversation reads top down, oldest first.
        rows: [...g.rows].sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at)),
      };
    });
    list.sort((a, b) => (Number(b.attention) - Number(a.attention))
      || (Number(!a.requestId) - Number(!b.requestId))
      || String(a.recipient || '').localeCompare(String(b.recipient || ''))
      || String(a.title).localeCompare(String(b.title)));
    return list;
  }, [rows]);

  const shownGroups = groups
    .filter((g) => filter !== 'attention' || g.attention)
    .map((g) => ({ ...g, shown: g.rows.filter((r) => rowMatches(r, filter)) }))
    .filter((g) => g.shown.length > 0);

  const totals = {
    requests: groups.filter((g) => g.requestId).length,
    sent: rows.filter((r) => r.direction === 'outbound').length,
    reminders: rows.filter(isReminder).length,
    replies: rows.filter((r) => r.direction === 'inbound').length,
    failed: rows.filter((r) => r.direction === 'outbound' && !r.sent_ok).length,
    attention: groups.filter((g) => g.attention).length,
  };

  // A request's own page shows its thread open. On the tab, folders open on
  // click, and open by themselves when a search or a narrow filter is on.
  const autoOpen = !!requestId || !!q || (filter !== 'all' && filter !== 'attention') || shownGroups.length <= 3;
  const isOpen = (key) => autoOpen || expanded.has(key);
  const toggle = (key) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const allOpen = shownGroups.length > 0 && shownGroups.every((g) => expanded.has(g.key));

  const afterSent = () => { load(); loadMonths(); };

  return (
    <div>
      {!requestId && (
        <div className="bg-white border border-slate-200 px-4 py-3 mb-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500" htmlFor="ur-corr-month">Month</label>
            <select id="ur-corr-month" value={month || ''} onChange={(e) => { setMonth(e.target.value); setExpanded(new Set()); setOpenId(null); }}
              className="border border-slate-300 px-2 py-1 text-sm bg-white focus:outline-none focus:border-red-700">
              {months.map((m) => (
                <option key={m.month} value={m.month}>
                  {monthLabel(m.month)} · {m.requests} request{m.requests === 1 ? '' : 's'}, {m.emails} email{m.emails === 1 ? '' : 's'}{m.failed ? `, ${m.failed} failed` : ''}
                </option>
              ))}
              <option value="all">All months</option>
            </select>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search subject, recipient, department, FDR or request"
              aria-label="Search correspondence"
              className="flex-1 min-w-[16rem] border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:border-red-700" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(([id, label]) => (
              <button key={id} onClick={() => setFilter(id)}
                className={`px-3 py-1 text-xs font-mono uppercase tracking-widest border ${filter === id ? 'border-red-700 text-red-700 bg-red-50' : 'border-slate-300 text-slate-600 hover:border-slate-500'}`}>
                {label}{id === 'attention' && totals.attention ? ` (${totals.attention})` : ''}{id === 'failed' && totals.failed ? ` (${totals.failed})` : ''}
              </button>
            ))}
            {!autoOpen && shownGroups.length > 0 && (
              <button onClick={() => setExpanded(allOpen ? new Set() : new Set(shownGroups.map((g) => g.key)))}
                className="ml-auto text-xs text-slate-600 hover:text-slate-900">{allOpen ? 'Collapse all' : 'Expand all'}</button>
            )}
          </div>
          <div className="text-xs text-slate-500">
            {month === 'all' ? 'All months' : monthLabel(month)}: {totals.requests} request{totals.requests === 1 ? '' : 's'},
            {' '}{totals.sent} sent, {totals.reminders} reminder{totals.reminders === 1 ? '' : 's'}, {totals.replies} repl{totals.replies === 1 ? 'y' : 'ies'}
            {totals.failed ? <span className="text-red-700">, {totals.failed} failed</span> : null}
            {q ? <> · matching “{q}”</> : null}
          </div>
        </div>
      )}

      {error && <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm font-mono mb-3">{error}</div>}
      {loading && <div className="text-xs text-slate-500">Loading…</div>}
      {!loading && !error && shownGroups.length === 0 && (
        <div className="bg-white border border-slate-200 px-4 py-6 text-sm text-slate-600">
          {rows.length === 0 && !q && (month === 'all' || !months.length || requestId)
            ? 'Nothing sent yet. Every email this module sends, and every reply that comes back, will appear here.'
            : 'Nothing matches. Try another month, filter or search.'}
        </div>
      )}

      <div className="space-y-3">
        {shownGroups.map((g) => (
          <div key={g.key} className={`bg-white border ${g.attention ? 'border-red-200' : 'border-slate-200'}`}>
            {!requestId && (
              <div className="px-4 py-2.5 flex items-start gap-3 flex-wrap">
                <button onClick={() => toggle(g.key)} className="flex-1 min-w-0 text-left" aria-expanded={isOpen(g.key)}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!autoOpen && <span className="font-mono text-xs text-slate-400 w-3">{isOpen(g.key) ? '▾' : '▸'}</span>}
                    <span className="text-sm font-semibold text-slate-900">{g.title}</span>
                    {g.status && (
                      <span className={`px-1.5 py-px text-[10px] font-mono uppercase tracking-wide ${g.overdue ? 'bg-red-50 text-red-700' : g.open ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                        {g.overdue ? 'Overdue' : (STATUS_LABEL[g.status] || g.status)}
                      </span>
                    )}
                    {g.failed > 0 && <span className="px-1.5 py-px text-[10px] font-mono uppercase tracking-wide bg-red-50 text-red-700">{g.failed} failed</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 pl-5">
                    {[g.recipient, g.period ? `Reporting period ${g.period}` : null, g.dueAt ? `Due ${fmtDay(g.dueAt)}` : null].filter(Boolean).join(' · ')}
                    {(g.recipient || g.period || g.dueAt) ? ' · ' : ''}
                    {g.counts.sent} sent, {g.counts.reminders} reminder{g.counts.reminders === 1 ? '' : 's'}{g.counts.byHand ? ` (${g.counts.byHand} by hand)` : ''}, {g.counts.replies} repl{g.counts.replies === 1 ? 'y' : 'ies'}
                  </div>
                </button>
                {g.requestId && g.open && (
                  <button onClick={() => setRemindFor({ id: g.requestId, title: g.title })}
                    className="border border-red-700 text-red-700 hover:bg-red-50 px-3 py-1 text-xs font-semibold whitespace-nowrap">
                    Send reminder
                  </button>
                )}
              </div>
            )}
            {isOpen(g.key) && g.shown.map((r) => (
              <div key={r.id} className="border-t border-slate-100">
                <button onClick={() => setOpenId(openId === r.id ? null : r.id)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-start gap-3">
                  <span className={`mt-0.5 px-1.5 py-px text-[10px] font-mono uppercase tracking-wide ${r.direction === 'inbound' ? 'bg-sky-50 text-sky-800' : r.sent_ok ? 'bg-slate-100 text-slate-700' : 'bg-red-50 text-red-700'}`}>
                    {r.direction === 'inbound' ? 'Reply' : (r.sent_ok ? 'Sent' : 'Failed')}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-900 truncate">{r.subject}</span>
                    <span className="block text-xs text-slate-500 truncate">
                      {KIND_LABEL[r.kind] || r.kind} · {SOURCE_LABEL[r.source] || r.source}
                      {r.source === 'manual' && r.created_by_name ? ` by ${r.created_by_name}` : ''}
                      {r.direction === 'inbound' ? ` · from ${r.from_address || 'unknown'}` : ` · to ${(r.to_emails || []).join(', ')}`}
                      {(r.cc_emails || []).length ? ` · cc ${(r.cc_emails || []).length}` : ''}
                      {(r.attachments || []).length ? ` · ${(r.attachments || []).length} attachment${(r.attachments || []).length === 1 ? '' : 's'}` : ''}
                      {r.purged_at ? ' · PURGED' : ''}
                    </span>
                  </span>
                  <span className="text-xs text-slate-500 font-mono whitespace-nowrap">{fmt(r.sent_at)}</span>
                </button>
                {openId === r.id && <EmailView id={r.id} onChanged={load} />}
              </div>
            ))}
          </div>
        ))}
      </div>

      {remindFor && (
        <ManualReminderDialog requestId={remindFor.id} title={remindFor.title}
          onClose={() => setRemindFor(null)} onSent={afterSent} />
      )}
    </div>
  );
}

function EmailView({ id, onChanged }) {
  const [c, setC] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [attest, setAttest] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/universe-requests/correspondence/${id}`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setC(d.correspondence || null)).catch(() => setC(null));
  }, [id]);
  if (!c) return <div className="px-4 py-3 text-xs text-slate-500">Loading…</div>;
  const purge = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/universe-requests/correspondence/${id}/purge`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attestation: attest, confirm: confirm.trim().toUpperCase() }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'Purge failed');
      setPurgeOpen(false); onChanged();
      setC({ ...c, purged_at: new Date().toISOString(), purge_attestation: attest, body_html: null, body_text: null, meta: { ...(c.meta || {}), purge_certificate: d.certificate } });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const cert = c.meta?.purge_certificate;
  return (
    <div className="px-4 pb-4 pt-1 bg-slate-50/60 border-t border-slate-100">
      <div className="text-xs text-slate-600 grid grid-cols-[70px_1fr] gap-y-0.5 mb-3">
        <span className="font-mono uppercase text-slate-400">From</span><span>{c.from_address || 'the platform, on behalf of the organization'}</span>
        <span className="font-mono uppercase text-slate-400">To</span><span>{(c.to_emails || []).join(', ')}</span>
        {(c.cc_emails || []).length > 0 && <><span className="font-mono uppercase text-slate-400">Cc</span><span>{c.cc_emails.join(', ')}</span></>}
        {c.meta?.reply_to && <><span className="font-mono uppercase text-slate-400">Reply to</span><span className="font-mono">{c.meta.reply_to}</span></>}
        <span className="font-mono uppercase text-slate-400">When</span><span>{fmt(c.sent_at)}{c.created_by_name ? ` · by ${c.created_by_name}` : ''}</span>
        {(c.attachments || []).length > 0 && (
          <><span className="font-mono uppercase text-slate-400">Files</span>
            <span>{c.attachments.map((a, i) => <span key={i} className={`inline-block mr-2 ${a.purged ? 'line-through text-slate-400' : ''}`}>{a.name}{a.size ? ` (${Math.round(a.size / 1024)} KB)` : ''}</span>)}</span></>
        )}
      </div>
      {c.purged_at ? (
        <div className="border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700">
          <div className="font-mono uppercase tracking-widest text-red-700 mb-1">Purged {fmt(c.purged_at)}{c.purged_by_name ? ` by ${c.purged_by_name}` : ''}</div>
          <div className="mb-1"><span className="text-slate-500">Attestation:</span> {c.purge_attestation}</div>
          {cert && <div className="text-slate-500">Certificate: {cert.files?.length || 0} file{cert.files?.length === 1 ? '' : 's'} destroyed, body destroyed, {cert.purged_at}.{cert.files?.length ? ` Hashes: ${cert.files.map((f) => String(f.sha256 || '').slice(0, 12)).join(', ')}` : ''}</div>}
        </div>
      ) : (
        <>
          {c.body_html
            ? <iframe title="email" sandbox="" srcDoc={c.body_html} className="w-full h-[26rem] bg-white border border-slate-200" />
            : <pre className="whitespace-pre-wrap text-xs text-slate-800 bg-white border border-slate-200 px-3 py-2 max-h-[26rem] overflow-y-auto">{c.body_text}</pre>}
          {c.direction === 'inbound' && (
            <div className="mt-3">
              {!purgeOpen ? (
                <button onClick={() => setPurgeOpen(true)} className="text-xs font-semibold text-red-700 hover:text-red-900">
                  Purge this reply (PHI or a universe was sent by email)
                </button>
              ) : (
                <div className="border border-red-300 bg-red-50 p-3 space-y-2">
                  <div className="text-xs text-red-900">
                    This destroys the body and every attached file. The record keeps who sent it, when, the subject, your attestation and a certificate with the file hashes. It cannot be undone.
                  </div>
                  <textarea value={attest} onChange={(e) => setAttest(e.target.value)} rows={3}
                    placeholder="Attestation: what this reply carried, why it is being destroyed, and that no copy was kept."
                    className="w-full border border-red-300 px-2 py-1 text-sm" />
                  <div className="flex items-center gap-2">
                    <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type PURGE" className="border border-red-300 px-2 py-1 text-sm font-mono w-32" />
                    <button disabled={busy || attest.trim().length < 20 || confirm.trim().toUpperCase() !== 'PURGE'} onClick={purge}
                      className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-3 py-1.5 text-xs font-semibold">{busy ? 'Purging…' : 'Purge and attest'}</button>
                    <button onClick={() => setPurgeOpen(false)} className="text-xs text-slate-600">Cancel</button>
                  </div>
                  {err && <div className="text-xs text-red-800 font-mono">{err}</div>}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
